import React from 'react';
import PropTypes from 'prop-types';

var classCallCheck = function classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
};

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};

var inherits = function inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
};

var possibleConstructorReturn = function possibleConstructorReturn(self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return call && (typeof call === "object" || typeof call === "function") ? call : self;
};
/* eslint-env node, browser */


var noOutlineStyles = {
  outline: 'none'
};

var FocusWithin = function (_React$Component) {
  inherits(FocusWithin, _React$Component);

  function FocusWithin() {
    var _temp, _this, _ret;

    classCallCheck(this, FocusWithin);

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    return _ret = (_temp = (_this = possibleConstructorReturn(this, _React$Component.call.apply(_React$Component, [this].concat(args))), _this), _this.state = {
      focused: false
    }, _this._isMounted = false, _this._lastBlurEvent = null, _this.ref = React.createRef(), _this._onFocusIn = function () {
      if (_this._isMounted && _this._lastBlurEvent && _this.isInsideNode(_this.ref.current, _this._lastBlurEvent.target) && !_this.isInsideNode(_this.ref.current, document.activeElement)) {
        _this.setState({
          focused: false
        }, function () {
          document.removeEventListener('focusin', _this._onFocusIn);

          _this.props.onBlur(_this._lastBlurEvent);
        });
      }
    }, _this.onFocus = function (evt) {
      var onFocus = _this.props.onFocus;
      var focused = _this.state.focused;
      /**
       * If it's not focused yet we'll set the state to `focused: true`
       */

      if (!focused) {
        _this.setState({
          focused: true
        }, function () {
          /**
           * Attach a native event listener to the document. We have to use `focusin` since
           * native `focus` event doesn't bubble. See
           * https://developer.mozilla.org/en-US/docs/Web/Events/focusin and
           * https://developer.mozilla.org/en-US/docs/Web/Events/focus
           */
          document.addEventListener('focusin', _this._onFocusIn);
          onFocus(evt);
        });
      }
    }, _this.onBlur = function (evt) {
      evt.persist(); // Persist the original event since it will be fired later

      _this._lastBlurEvent = evt;
    }, _this.isInsideNode = function (parentNode, node) {
      if (process.env.NODE_ENV === 'development') {
        if (parentNode == null || Object(parentNode).nodeType !== 1) {
          throw new Error('A ref to a valid DOM Node must be supplied to' + ' FocusWithin.\n' + ' You have probably provided a ref to a React Element.\n See https://reactjs.org/docs/react-api.html#refs');
        }
      } // return false in case parentNode does not exist


      if (parentNode == null) {
        return false;
      }

      return parentNode.contains(node);
    }, _temp), possibleConstructorReturn(_this, _ret);
  }

  FocusWithin.prototype.componentDidMount = function componentDidMount() {
    /**
     * In order for document.body to receive focus events
     * it needs to be focusable. Adding `tabindex="-1"` makes it focusable
     * but prevents it from receiving the focus on user interaction.
     */
    if (document != null) {
      document.querySelector('body').setAttribute('tabindex', '-1');
    }
    /* Mark as mounted */


    this._isMounted = true;
  };

  FocusWithin.prototype.componentWillUnmount = function componentWillUnmount() {
    /* Mark as unmounted */
    this._isMounted = false;
    /* Since the onBlur for the unmounted component will never fire, we need to cleanup here. */

    document.removeEventListener('focusin', this._onFocusIn);
  };
  /**
   * Calls `focus` method on the container node
   *
   * @public
   * @method focus
   * */


  FocusWithin.prototype.focus = function focus() {
    var node = this.ref.current;

    if (node != null && typeof node.focus === 'function') {
      node.focus();
    }
  };
  /**
   * Event handler that fires if the FocusEvent bubbled up to the document.
   *
   * @private
   * @method _onFocusIn
   *
   * We check if 3 conditions are met:
   * 1. Current state is focused
   * 2. Blur occured inside the container
   * 3. Focus occured outside of the container
   * 4. Component is still mounted to the DOM
   *
   * In this case we fire `onBlur` callback.
   */

  /**
   * @private
   * @method onFocus
   */

  /**
   * @private
   * @method onBlur
   */

  /**
   * Checks if the parentNode contains the node
   *
   * @private
   * @method isInsideNode
   * @param parentNode
   * @param node
   * @returns {boolean}
   */


  FocusWithin.prototype.render = function render() {
    var children = this.props.children;
    var focused = this.state.focused;
    var events = {
      onFocus: this.onFocus,
      onBlur: this.onBlur
    };

    if (typeof children === 'function') {
      return React.cloneElement(children({
        focused: focused,
        getRef: this.ref
      }), events);
    }

    return React.createElement('div', _extends({
      ref: this.ref,
      style: noOutlineStyles
    }, events), children);
  };

  return FocusWithin;
}(React.Component);

FocusWithin.propTypes = {
  /**
   * Function has the following signature:
   * `({ focused: Boolean, getRef: Function }) => React.Element`
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func
};
FocusWithin.defaultProps = {
  onBlur: function onBlur() {},
  onFocus: function onFocus() {}
};
export default FocusWithin;